// Selection detection utilities for map editor
// Port of packages/maps/src/composables/useSelectionDetection.ts

import { gridPointToPixel } from './coordinate-transforms.js';

/**
 * @typedef {import('../types/jsdoc-types.js').GridPoint} GridPoint
 * @typedef {import('../types/jsdoc-types.js').Opening} Opening
 * @typedef {import('../types/jsdoc-types.js').Light} Light
 * @typedef {import('../types/jsdoc-types.js').Cover} Cover
 * @typedef {import('../types/jsdoc-types.js').TokenDirection} TokenDirection
 */

/**
 * @typedef {object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * Check if a point is inside a polygon using ray casting algorithm
 * @param {Point} point
 * @param {Array<Point>} polygon
 * @returns {boolean}
 */
function isPointInPolygon(point, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pi = polygon[i];
    const pj = polygon[j];
    if (!pi || !pj) continue;
    const xi = pi.x,
      yi = pi.y;
    const xj = pj.x,
      yj = pj.y;

    const intersect = yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Check if a point is near a position
 * @param {Point} point
 * @param {Point} worldPos
 * @param {number} zoom
 * @returns {boolean}
 */
function isPointNearPosition(point, worldPos, zoom) {
  const clickRadius = 12 / zoom;
  const dx = point.x - worldPos.x;
  const dy = point.y - worldPos.y;
  return Math.sqrt(dx * dx + dy * dy) <= clickRadius;
}

/**
 * Check if a point is near a line segment
 * @param {Point} point
 * @param {Point} lineStart
 * @param {Point} lineEnd
 * @param {number} zoom
 * @returns {boolean}
 */
function isPointNearLineSegment(point, lineStart, lineEnd, zoom) {
  const lineThreshold = 8 / zoom;
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    return isPointNearPosition(lineStart, point, zoom);
  }

  const t = Math.max(0, Math.min(1, ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSquared));
  const projX = lineStart.x + t * dx;
  const projY = lineStart.y + t * dy;
  const distX = point.x - projX;
  const distY = point.y - projY;
  return Math.sqrt(distX * distX + distY * distY) <= lineThreshold;
}

/**
 * Find wall at world position
 * @param {Point} worldPos
 * @param {Array<{id?: string, points: Array<GridPoint>}>} walls
 * @param {number} gridSize
 * @param {number} zoom
 * @returns {number|null}
 */
export function findWallAtPosition(worldPos, walls, gridSize, zoom) {
  for (let i = walls.length - 1; i >= 0; i--) {
    const wall = walls[i];
    if (!wall?.points) continue;

    for (const gridPoint of wall.points) {
      const point = gridPointToPixel(gridPoint, gridSize);
      if (isPointNearPosition(point, worldPos, zoom)) {
        return i;
      }
    }

    for (let j = 0; j < wall.points.length - 1; j++) {
      const wp1 = wall.points[j];
      const wp2 = wall.points[j + 1];
      if (!wp1 || !wp2) continue;
      const p1 = gridPointToPixel(wp1, gridSize);
      const p2 = gridPointToPixel(wp2, gridSize);
      if (isPointNearLineSegment(worldPos, p1, p2, zoom)) {
        return i;
      }
    }
  }
  return null;
}

/**
 * Find light at world position
 * @param {Point} worldPos
 * @param {Light[]} lights
 * @param {number} gridSize
 * @param {number} zoom
 * @returns {number|null}
 */
export function findLightAtPosition(worldPos, lights, gridSize, zoom) {
  const clickTolerance = 20 / zoom;
  for (let i = lights.length - 1; i >= 0; i--) {
    const light = lights[i];
    if (!light || light.col === undefined || light.row === undefined) continue;
    const lightX = light.col * gridSize + gridSize / 2;
    const lightY = light.row * gridSize + gridSize / 2;
    const dx = lightX - worldPos.x;
    const dy = lightY - worldPos.y;
    if (Math.sqrt(dx * dx + dy * dy) <= clickTolerance) {
      return i;
    }
  }
  return null;
}

/**
 * @typedef {object} WallSegment
 * @property {{x: number, y: number}} start - Start pixel coordinate
 * @property {{x: number, y: number}} end - End pixel coordinate
 */

/**
 * Check if a segment is grid-aligned (horizontal or vertical)
 * @param {Point} p1
 * @param {Point} p2
 * @returns {boolean}
 */
function isSegmentGridAligned(p1, p2) {
  const tolerance = 5;
  return Math.abs(p1.x - p2.x) < tolerance || Math.abs(p1.y - p2.y) < tolerance;
}

/**
 * Get the single grid-unit wall segment at a cursor position.
 * Finds which wall segment (pair of adjacent points) the cursor is near,
 * and returns a single grid-unit-length sub-segment within that wall segment.
 * @param {Point} worldPos
 * @param {Array<{id?: string, points: Array<GridPoint>}>} walls
 * @param {number} gridSize
 * @param {number} zoom
 * @returns {WallSegment|null}
 */
export function getWallSegmentAtPoint(worldPos, walls, gridSize, zoom) {
  const clickTolerance = 15 / zoom;

  for (const wall of walls) {
    if (!wall?.points || wall.points.length < 2) continue;

    for (let i = 0; i < wall.points.length - 1; i++) {
      const wp1 = wall.points[i];
      const wp2 = wall.points[i + 1];
      if (!wp1 || !wp2) continue;
      const p1 = gridPointToPixel(wp1, gridSize);
      const p2 = gridPointToPixel(wp2, gridSize);

      // Only consider grid-aligned segments
      if (!isSegmentGridAligned(p1, p2)) continue;

      // Calculate distance from point to line segment
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const lengthSquared = dx * dx + dy * dy;

      if (lengthSquared === 0) continue;

      const t = Math.max(0, Math.min(1, ((worldPos.x - p1.x) * dx + (worldPos.y - p1.y) * dy) / lengthSquared));
      const projX = p1.x + t * dx;
      const projY = p1.y + t * dy;
      const distX = worldPos.x - projX;
      const distY = worldPos.y - projY;
      const distance = Math.sqrt(distX * distX + distY * distY);

      if (distance <= clickTolerance) {
        // Return single grid unit segment based on cursor position
        const isHorizontal = Math.abs(p1.y - p2.y) < 5;
        const isVertical = Math.abs(p1.x - p2.x) < 5;

        if (isHorizontal) {
          const segmentY = p1.y;
          const gridX = Math.round(worldPos.x / gridSize) * gridSize;
          const minX = Math.min(p1.x, p2.x);
          const maxX = Math.max(p1.x, p2.x);
          const clampedGridX = Math.max(minX, Math.min(maxX - gridSize, gridX));

          return {
            start: { x: clampedGridX, y: segmentY },
            end: { x: clampedGridX + gridSize, y: segmentY },
          };
        } else if (isVertical) {
          const segmentX = p1.x;
          const gridY = Math.round(worldPos.y / gridSize) * gridSize;
          const minY = Math.min(p1.y, p2.y);
          const maxY = Math.max(p1.y, p2.y);
          const clampedGridY = Math.max(minY, Math.min(maxY - gridSize, gridY));

          return {
            start: { x: segmentX, y: clampedGridY },
            end: { x: segmentX, y: clampedGridY + gridSize },
          };
        }
      }
    }
  }

  return null;
}

/**
 * Find opening at world position
 * @param {Point} worldPos
 * @param {Opening[]} openings
 * @param {number} gridSize
 * @param {number} zoom
 * @returns {number|null}
 */
export function findOpeningAtPosition(worldPos, openings, gridSize, zoom) {
  for (let i = openings.length - 1; i >= 0; i--) {
    const opening = openings[i];
    if (!opening?.points || opening.points.length < 2) continue;

    for (let j = 0; j < opening.points.length - 1; j++) {
      const op1 = opening.points[j];
      const op2 = opening.points[j + 1];
      if (!op1 || !op2) continue;
      const p1 = gridPointToPixel(op1, gridSize);
      const p2 = gridPointToPixel(op2, gridSize);
      if (isPointNearLineSegment(worldPos, p1, p2, zoom)) {
        return i;
      }
    }
  }
  return null;
}

/**
 * Get token size multiplier (matches token-renderer.js values)
 * @param {string} size
 * @returns {number}
 */
function getTokenSizeMultiplier(size) {
  switch (size) {
    case 'tiny':
      return 0.5;
    case 'small':
      return 1.0;
    case 'medium':
      return 1.0;
    case 'large':
      return 2.0;
    case 'huge':
      return 3.0;
    case 'gargantuan':
      return 4.0;
    default:
      return 1.0;
  }
}

/**
 * Find monster (token) at world position using circular hit detection
 * @param {Point} worldPos
 * @param {Array<{col: number, row: number, size?: string}>} tokens
 * @param {number} gridSize
 * @returns {number|null}
 */
export function findMonsterAtPosition(worldPos, tokens, gridSize) {
  for (let i = tokens.length - 1; i >= 0; i--) {
    const token = tokens[i];
    if (!token) continue;
    const sizeMultiplier = getTokenSizeMultiplier(token.size || 'medium');
    const tokenRadius = (gridSize * sizeMultiplier) / 2;
    const centerX = token.col * gridSize + tokenRadius;
    const centerY = token.row * gridSize + tokenRadius;
    const dx = worldPos.x - centerX;
    const dy = worldPos.y - centerY;
    if (Math.sqrt(dx * dx + dy * dy) <= tokenRadius) {
      return i;
    }
  }
  return null;
}

/**
 * Check if a world-space point is inside a token's direction indicator triangle
 * @param {Point} worldPos
 * @param {{ col: number, row: number, size?: string, direction?: string }} token
 * @param {number} gridSize
 * @returns {boolean}
 */
export function isPointInDirectionIndicator(worldPos, token, gridSize) {
  const sizeMultiplier = getTokenSizeMultiplier(token.size || 'medium');
  const tokenRadius = (gridSize * sizeMultiplier) / 2;

  // Token center
  const tokenX = token.col * gridSize + tokenRadius;
  const tokenY = token.row * gridSize + tokenRadius;

  // Triangle dimensions (same as token-renderer.js)
  const triangleWidth = tokenRadius / 3;
  const triangleHeight = tokenRadius / 5;

  /** @type {Record<string, number>} */
  const ROTATION_ANGLES = {
    down: 0,
    right: -Math.PI / 2,
    up: Math.PI,
    left: Math.PI / 2,
    'down-right': -Math.PI / 4,
    'down-left': Math.PI / 4,
    'up-right': (-3 * Math.PI) / 4,
    'up-left': (3 * Math.PI) / 4,
  };

  const direction = token.direction || 'down';
  const rotationAngle = ROTATION_ANGLES[direction];
  if (rotationAngle === undefined) return false;

  // Transform point to token's local coordinate system
  const dx = worldPos.x - tokenX;
  const dy = worldPos.y - tokenY;

  // Apply inverse rotation to point
  const cos = Math.cos(-rotationAngle);
  const sin = Math.sin(-rotationAngle);
  const localX = dx * cos - dy * sin;
  const localY = dx * sin + dy * cos;

  // Triangle vertices in local coordinates (pointing down initially)
  const tipX = 0;
  const tipY = tokenRadius;
  const baseLeftX = -triangleWidth / 2;
  const baseLeftY = tokenRadius - triangleHeight;
  const baseRightX = triangleWidth / 2;
  const baseRightY = tokenRadius - triangleHeight;

  // Check if point is inside triangle using barycentric coordinates
  const v0x = baseRightX - baseLeftX;
  const v0y = baseRightY - baseLeftY;
  const v1x = tipX - baseLeftX;
  const v1y = tipY - baseLeftY;
  const v2x = localX - baseLeftX;
  const v2y = localY - baseLeftY;

  const dot00 = v0x * v0x + v0y * v0y;
  const dot01 = v0x * v1x + v0y * v1y;
  const dot02 = v0x * v2x + v0y * v2y;
  const dot11 = v1x * v1x + v1y * v1y;
  const dot12 = v1x * v2x + v1y * v2y;

  const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);
  const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
  const v = (dot00 * dot12 - dot01 * dot02) * invDenom;

  return u >= 0 && v >= 0 && u + v <= 1;
}

/**
 * Convert an angle (from atan2) to a direction (cardinal or diagonal)
 * @param {number} angle - Angle in radians
 * @returns {TokenDirection}
 */
export function getDirectionFromAngle(angle) {
  let degrees = (angle * 180) / Math.PI;
  if (degrees < 0) degrees += 360;

  if (degrees >= 337.5 || degrees < 22.5) return 'right';
  if (degrees >= 22.5 && degrees < 67.5) return 'down-right';
  if (degrees >= 67.5 && degrees < 112.5) return 'down';
  if (degrees >= 112.5 && degrees < 157.5) return 'down-left';
  if (degrees >= 157.5 && degrees < 202.5) return 'left';
  if (degrees >= 202.5 && degrees < 247.5) return 'up-left';
  if (degrees >= 247.5 && degrees < 292.5) return 'up';
  return 'up-right';
}

/**
 * Find cover at world position
 * @param {Point} worldPos
 * @param {Cover[]} covers
 * @param {number} gridSize
 * @param {number} zoom
 * @returns {number|null}
 */
export function findCoverAtPosition(worldPos, covers, gridSize, zoom) {
  for (let i = covers.length - 1; i >= 0; i--) {
    const cover = covers[i];
    if (!cover?.isComplete || !cover.points || cover.points.length < 3) continue;

    const pixelPoints = cover.points.map((p) => gridPointToPixel(p, gridSize));

    // Check if point is inside polygon
    if (isPointInPolygon(worldPos, pixelPoints)) {
      return i;
    }

    // Also check if near any control point
    for (const point of pixelPoints) {
      if (isPointNearPosition(point, worldPos, zoom)) {
        return i;
      }
    }
  }
  return null;
}
